import { ComponentType, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { EnvironmentInjector, Injectable, InjectionToken, Injector } from '@angular/core';
import { filter, finalize, Observable, shareReplay, Subject, take } from 'rxjs';
import { CoreModalContainerComponent } from '../components/core-modal-container/core-modal-container.component';

export const MODAL_REF = new InjectionToken<ModalRef>('MODAL_REF');
export const MODAL_DATA = new InjectionToken<any>('MODAL_DATA');

export enum ModalTone {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  CONFIRM = 'confirm',
}

export const MODAL_TONE_ICONS: Record<ModalTone, string> = {
  [ModalTone.ERROR]: 'fa-solid fa-circle-exclamation',
  [ModalTone.WARNING]: 'fa-solid fa-triangle-exclamation',
  [ModalTone.INFO]: 'fa-solid fa-circle-info',
  [ModalTone.CONFIRM]: 'fa-solid fa-circle-question',
};

export interface ModalConfig<TData = unknown> {
  data?: TData;
  dataBinding?: string[];
  title?: string;
  subtitle?: string;
  tone?: ModalTone;
  position?: ModalPositionPreset;
  closeOnEscOrBackdropClick?: boolean;
  hasActions?: ModalBaseActions;
  customActions?: ModalAction[];
  allowInteractionBelow?: boolean;
  animation?: boolean;
  panelClass?: string[];
  animClass?: string[];
  backdropClass?: string;
  hasBackdrop?: boolean;
  internalScroll?: boolean;
  dimensions?: ModalDimensions;
}

export interface ModalRef<TResult = unknown> {
  close: (result?: TResult) => void;
  afterClosed$: Observable<TResult | undefined>;
}
export interface ModalStackItem {
  component: any;
  injector: Injector;
  modalRef: ModalRef<any>;
  data?: any;
  dataBinding?: string[];
  title?: string;
  subtitle?: string;
  tone?: ModalTone;
  panelClass: string[];
  animation: boolean;
  closeOnEscOrBackdropClick: boolean;
  allowInteractionBelow: boolean;
  isActive: boolean;
  actions: ModalAction[];
  internalScroll: boolean;
  dimensions?: ModalDimensions;
}

export enum ModalPositionPreset {
  CENTER = 'center',
  FULL_RIGHT = 'full-right',
  FULL_LEFT = 'full-left',
  FULL_RIGHT_WITH_HEADER = 'full-right-with-header',
  FULL_LEFT_WITH_HEADER = 'full-left-with-header',
  TOP = 'top',
  BOTTOM = 'bottom'
}
export enum ModalBaseActions {
  NONE = 'none',
  CONFIRM = 'confirm',
  CANCEL = 'cancel',
  CONFIRM_CANCEL = 'confirm_cancel',
  CLOSE = 'close'
}

interface ModalStrategy {
  panelClass?: string[];
  animClass?: string[];
}

export interface ModalAction {
  label: string;
  class?: string[];
  value?: unknown;
  onClick?: () => void;
  closeOnClick?: boolean;
  disabled?: boolean | (() => boolean);
}

const MODAL_STRATEGIES: Record<ModalPositionPreset, ModalStrategy> = {
  [ModalPositionPreset.CENTER]: { panelClass: ['modal-center'], animClass: ['anim-fade-scale'] },
  [ModalPositionPreset.FULL_RIGHT]: { panelClass: ['modal-full-right'], animClass: ['anim-slide-right'] },
  [ModalPositionPreset.FULL_LEFT]: { panelClass: ['modal-full-left'], animClass: ['anim-slide-left'] },
  [ModalPositionPreset.FULL_RIGHT_WITH_HEADER]: { panelClass: ['modal-full-right-with-header'], animClass: ['anim-slide-right'] },
  [ModalPositionPreset.FULL_LEFT_WITH_HEADER]: { panelClass: ['modal-full-left-with-header'], animClass: ['anim-slide-left'] },
  [ModalPositionPreset.TOP]: { panelClass: ['modal-top'], animClass: ['anim-slide-top'] },
  [ModalPositionPreset.BOTTOM]: { panelClass: ['modal-bottom'], animClass: ['anim-slide-bottom'] }
};
const sharedButtonClasses = ['eidos-button', 'eidos-button-outlined', 'eidos-button-big'];
const MODAL_ACTIONS: Record<ModalBaseActions, ModalAction[]> = {
  [ModalBaseActions.NONE]: [],
  [ModalBaseActions.CONFIRM]: [{ label: 'CONFIRM', class: [...sharedButtonClasses, 'eidos-button-success'], value: true }],
  [ModalBaseActions.CANCEL]: [{ label: 'CANCEL', class: [...sharedButtonClasses, 'eidos-button-cancel'], value: false }],
  [ModalBaseActions.CONFIRM_CANCEL]: [{ label: 'CANCEL', class: [...sharedButtonClasses, 'eidos-button-cancel'], value: false }, { label: 'CONFIRM', class: [...sharedButtonClasses, 'eidos-button-success'], value: true }],
  [ModalBaseActions.CLOSE]: [{ label: 'CLOSE', class: [...sharedButtonClasses, 'res-button-dark'], value: false }]
};

function normalizeModalAction(action: ModalAction): ModalAction {
  return {
    ...action,
    class: action.class?.length ? action.class : [...sharedButtonClasses, 'res-button-dark'],
    closeOnClick: action.closeOnClick ?? true
  };
}

function resolveModalActions(config?: ModalConfig): ModalAction[] {
  const baseActions = MODAL_ACTIONS[config?.hasActions ?? ModalBaseActions.NONE];
  const customActions = (config?.customActions ?? []).map(normalizeModalAction);
  return [...baseActions, ...customActions];
}

export interface ModalDimensions {
  width?: string | number;
  height?: string | number;

  minWidth?: string | number;
  minHeight?: string | number;

  maxWidth?: string | number;
  maxHeight?: string | number;
}

@Injectable({
  providedIn: 'root'
})
export class CoreModalService {
  private overlayRef: OverlayRef | null = null;
  private container!: CoreModalContainerComponent;

  private stack: ModalStackItem[] = [];

  get hasOpenModals(): boolean {
    return this.stack.length > 0;
  }

  constructor(
    private overlay: Overlay,
    private envInjector: EnvironmentInjector
  ) { }

  open<TComponent, TData = unknown, TResult = unknown>(
    component: ComponentType<TComponent>,
    config?: ModalConfig<TData>
  ): Observable<TResult | undefined> {
    this.ensureOverlay(config);

    const close$ = new Subject<TResult | undefined>();

    const modalRef: ModalRef<TResult> = {
      close: (result?: TResult) => close$.next(result),
      afterClosed$: close$.pipe(take(1), shareReplay(1))
    };

    const injector = Injector.create({
      providers: [
        { provide: MODAL_REF, useValue: modalRef },
        { provide: MODAL_DATA, useValue: config?.data }
      ],
      parent: this.envInjector
    });

    const preset = config?.position || ModalPositionPreset.CENTER;
    const strategy = MODAL_STRATEGIES[preset];

    const panelClass =  config?.panelClass ?? strategy.panelClass ?? [];
    const hasAnimations = config?.animation ?? true;
    const animClass = config?.animClass ?? strategy.animClass ?? [];
    const toneClass = config?.tone ? [`modal-tone-${config.tone}`] : [];

    const item: ModalStackItem = {
      component,
      injector,
      modalRef,
      data: config?.data,
      dataBinding: config?.dataBinding,
      title: config?.title,
      subtitle: config?.subtitle,
      tone: config?.tone,
      panelClass: [...panelClass, ...toneClass, ...(hasAnimations ? animClass : [])],
      animation: config?.animation ?? true,
      closeOnEscOrBackdropClick: config?.closeOnEscOrBackdropClick ?? false,
      isActive: true,
      allowInteractionBelow: config?.allowInteractionBelow ?? false,
      actions: resolveModalActions(config),
      internalScroll: config?.internalScroll ?? false,
      dimensions: config?.dimensions,
    };

    this.stack.push(item);
    this.container.push(item);
    this.updateInteractivity();

    close$.pipe(
      take(1),
      finalize(() => this.remove(item))
    ).subscribe();

    return modalRef.afterClosed$;
  }

  closeTop() {
    const top = this.stack[this.stack.length - 1];
    top?.modalRef.close();
  }

  private remove(item: ModalStackItem) {
    const index = this.stack.indexOf(item);
    if (index === -1) return;

    this.stack.splice(index, 1);

    if (index === this.container.length - 1) {
      this.container.pop();
    } else {
      this.container.removeAt(index);
    }

    this.updateInteractivity();

    if (this.stack.length === 0 && item.animation) {
      this.animateBackdropClosing();
    }
  }

  private animateBackdropClosing() {
    const backdrop = this.overlayRef?.backdropElement;
    if (!backdrop) return;

    backdrop.classList.add('closing');
  }

  private ensureOverlay(config?: ModalConfig) {
    if (this.overlayRef) return;

    const overlayConfig: OverlayConfig = {
      hasBackdrop: config?.hasBackdrop?? true,
      backdropClass: config?.backdropClass ?? 'app-overlay-backdrop',
      panelClass: 'app-overlay-pane',
      // Fullscreen pane; content position comes from modal presets (.modal-center, etc.)
      positionStrategy: this.overlay.position().global().top('0').left('0').width('100%').height('100%')

    };

    this.overlayRef = this.overlay.create(overlayConfig);

    const portal = new ComponentPortal(
      CoreModalContainerComponent,
      null,
      this.envInjector
    );

    const containerRef = this.overlayRef.attach(portal);
    this.container = containerRef.instance;


    this.overlayRef.backdropClick().pipe(
      filter(() => {
        const top = this.stack[this.stack.length - 1];
        return top && top.closeOnEscOrBackdropClick;
      })
    ).subscribe(() => {
      this.closeTop();
    });

    this.overlayRef.keydownEvents().pipe(
      filter((e) => {
        const top = this.stack[this.stack.length - 1];
        return top && (e.key === 'Escape') && top.closeOnEscOrBackdropClick;
      })
    ).subscribe(e => {
      if (e.key === 'Escape') {
        this.closeTop();
      }
    });

    this.container.onAllClosed$
      .pipe(take(1))
      .subscribe(() => {
        this.container.clear();
        this.overlayRef?.dispose();
        this.overlayRef = null;
      });
  }

  private updateInteractivity() {
    let canInteract = true;
    for (let i = this.stack.length - 1; i >= 0; i--) {
      this.stack[i].isActive = canInteract;
      if (!this.stack[i].allowInteractionBelow) canInteract = false;
    }

    this.container.updateActiveStates(this.stack);
  }
}
